import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import MemberSearchDropdown from './member_search_dropdown';
import useOutsideClick from '../hooks/useOutsideClick';
import { componentText } from '../utils/i18n_utils';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 500;

const MemberSearch = ({
  query,
  results,
  loading,
  expandedMemberId,
  aggregates,
  aggregatesLoading,
  favoriteMemberIds,
  onQueryChange,
  onSelect,
  onClear,
  onExpandMember,
  onCollapseMember,
  onToggleMemberFavorite,
  defaultExpanded,
}) => {
  const [inputValue, setInputValue] = useState(query);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceTimer = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const onClearRef = useRef(onClear)
  onClearRef.current = onClear
  useEffect(() => () => onClearRef.current(), [])

  useEffect(() => {
    if (isExpanded && !defaultExpanded) inputRef.current?.focus();
  }, [isExpanded, defaultExpanded]);

  const handleClickOutside = useCallback(() => {
    clearTimeout(debounceTimer.current);
    setInputValue('');
    setHasSearched(false);
    onClear();
    if (!defaultExpanded) setIsExpanded(false);
  }, [onClear, defaultExpanded]);
  useOutsideClick(containerRef, handleClickOutside);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    clearTimeout(debounceTimer.current);

    if (value.length < MIN_QUERY_LENGTH) {
      setHasSearched(false);
      onClear();
      return;
    }

    debounceTimer.current = setTimeout(() => {
      setHasSearched(true);
      onQueryChange(value);
    }, DEBOUNCE_MS);
  };

  const handleSelect = (agg, memberId) => {
    setInputValue('');
    setHasSearched(false);
    onSelect({
      event_id: agg.event_id,
      aggregate_id: agg.aggregate_id,
      round_id: agg.round_id,
      member_id: memberId,
    });
    if (!defaultExpanded) setIsExpanded(false);
  };

  const handleClear = () => {
    clearTimeout(debounceTimer.current);
    setInputValue('');
    setHasSearched(false);
    onClear();
  };

  const handleExpand = () => {
    if (!isExpanded) setIsExpanded(true);
  };

  // Below the minimum the search never runs and everything is cleared, which
  // looks like a broken search - the panel opens on a hint instead.
  const isQueryTooShort = inputValue.length > 0 && inputValue.length < MIN_QUERY_LENGTH
  const showDropdown = isExpanded && (isQueryTooShort || results.length > 0 || loading || hasSearched)

  return (
    <div className={`lbv2__member-search${isExpanded ? ' is-expanded' : ''}`} ref={containerRef}>
      <div
        className="lbv2__member-search-input-wrapper themed-text"
        onClick={handleExpand}
      >
        <i
          className={`fa-regular fa-magnifying-glass lbv2__member-search-icon${isExpanded ? '' : ' themed-text'}`}
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          className="lbv2__member-search-input"
          placeholder={componentText('member_search.placeholder', 'Search for a Player')}
          value={inputValue}
          onChange={handleInputChange}
          aria-label={componentText('member_search.search_aria', 'Search player by name')}
          aria-autocomplete="list"
          aria-expanded={showDropdown}
        />
        {inputValue && (
          <button
            className="lbv2__member-search-clear"
            onClick={handleClear}
            aria-label={componentText('member_search.clear', 'Clear search')}
          >
            <i className="fa-regular fa-xmark" aria-hidden="true" />
          </button>
        )}
      </div>

      {showDropdown && (
        <MemberSearchDropdown
          results={results}
          loading={loading}
          isQueryTooShort={isQueryTooShort}
          minQueryLength={MIN_QUERY_LENGTH}
          expandedMemberId={expandedMemberId}
          aggregates={aggregates}
          aggregatesLoading={aggregatesLoading}
          favoriteMemberIds={favoriteMemberIds}
          onToggleMemberFavorite={onToggleMemberFavorite}
          onExpandMember={onExpandMember}
          onCollapseMember={onCollapseMember}
          onSelectAggregate={handleSelect}
        />
      )}
    </div>
  );
};

MemberSearch.propTypes = {
  query: PropTypes.string,
  results: PropTypes.arrayOf(PropTypes.shape({
    member_id: PropTypes.string,
    name: PropTypes.string,
    descriptor: PropTypes.string,
  })),
  loading: PropTypes.bool,
  expandedMemberId: PropTypes.string,
  aggregates: PropTypes.arrayOf(PropTypes.shape({
    event_id: PropTypes.string,
    event_name: PropTypes.string,
    aggregate_id: PropTypes.string,
    round_id: PropTypes.string,
    rank_display: PropTypes.string,
    rank_value_display: PropTypes.string,
    thru_display: PropTypes.string,
    last_round_score: PropTypes.string,
    last_round_course: PropTypes.string,
    last_round_label: PropTypes.string,
  })),
  aggregatesLoading: PropTypes.bool,
  favoriteMemberIds: PropTypes.instanceOf(Set),
  defaultExpanded: PropTypes.bool,
  onQueryChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onExpandMember: PropTypes.func.isRequired,
  onCollapseMember: PropTypes.func.isRequired,
  onToggleMemberFavorite: PropTypes.func.isRequired,
};

MemberSearch.defaultProps = {
  query: '',
  results: [],
  loading: false,
  expandedMemberId: null,
  aggregates: [],
  aggregatesLoading: false,
  favoriteMemberIds: new Set(),
  defaultExpanded: false,
};

export default MemberSearch;
